import Papa from 'papaparse';
import mammoth from 'mammoth';
import { createEmptyQuestion } from '../mock';

/**
 * CSV format expected columns:
 * type,question,option1,option2,option3,option4,option5,correct,answer
 * - type: one of mc-single | mc-multiple | truefalse | short | numerical | essay
 * - correct: for mc-single index (0-based) OR letter A/B/..; for mc-multiple use pipe (0|2) or A|C
 *            for truefalse: true|false
 * - answer: for short/numerical
 */
export const importFromCSV = (file) => new Promise((resolve, reject) => {
  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    complete: (res) => {
      try {
        const questions = res.data.map(row => rowToQuestion(row)).filter(Boolean);
        resolve(questions);
      } catch (e) { reject(e); }
    },
    error: reject,
  });
});

const letterToIdx = (s) => {
  const c = String(s).trim().toUpperCase().charCodeAt(0);
  if (c >= 65 && c <= 90) return c - 65;
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : 0;
};

const rowToQuestion = (row) => {
  const type = (row.type || 'mc-single').trim();
  const base = createEmptyQuestion(type, 1);
  base.question = (row.question || '').trim();
  base.title = (row.title || base.title).toString();
  const options = ['option1','option2','option3','option4','option5']
    .map(k => (row[k] || '').toString().trim())
    .filter(v => v.length > 0);

  if (type === 'mc-single') {
    base.options = options.length ? options : base.options;
    base.correct = letterToIdx(row.correct || 0);
  } else if (type === 'mc-multiple') {
    base.options = options.length ? options : base.options;
    base.corrects = String(row.correct || '').split('|').map(s => letterToIdx(s));
  } else if (type === 'truefalse') {
    base.correct = String(row.correct || 'true').toLowerCase() === 'true' ? 'true' : 'false';
  } else if (type === 'short') {
    base.answers = [(row.answer || '').toString()];
  } else if (type === 'numerical') {
    base.answer = (row.answer || '0').toString();
    base.tolerance = (row.tolerance || '0').toString();
  } else if (type === 'essay') {
    base.sampleAnswer = (row.answer || '').toString();
  }
  return base;
};

/**
 * DOCX/plain-text format:
 *   1. Question text goes here
 *   A. Option A
 *   B. Option B *
 *   C. Option C
 *   D. Option D
 * Blank line separates each question. Correct option marked with trailing `*`.
 * If no options found -> becomes short-answer with the first `Answer:` line.
 */
export const importFromDOCX = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const { value } = await mammoth.extractRawText({ arrayBuffer });
  return parseTextToQuestions(value);
};

export const importFromTXT = async (file) => {
  const text = await file.text();
  return parseTextToQuestions(text);
};

const parseTextToQuestions = (text) => {
  const blocks = text.split(/\n\s*\n/).map(b => b.trim()).filter(Boolean);
  const out = [];
  blocks.forEach((block, idx) => {
    const lines = block.split(/\n/).map(l => l.trim()).filter(Boolean);
    if (!lines.length) return;

    // Remove leading numbering like "1." or "1)"
    const qLine = lines[0].replace(/^\d+[\.\)]\s*/, '');
    const optLines = lines.slice(1).filter(l => /^[A-Ea-e][\.\)]/.test(l));
    if (optLines.length >= 2) {
      const options = optLines.map(l => l.replace(/^[A-Ea-e][\.\)]\s*/, '').replace(/\s*\*\s*$/, '').trim());
      const correct = optLines.findIndex(l => /\*\s*$/.test(l));
      const q = createEmptyQuestion('mc-single', idx + 1);
      q.question = qLine;
      q.options = options.concat(Array(Math.max(0, 5 - options.length)).fill('')).slice(0, 5);
      q.correct = correct >= 0 ? correct : 0;
      out.push(q);
      return;
    }
    // Short answer fallback
    const answerLine = lines.find(l => /^(answer|jawaban)\s*:/i.test(l));
    const q = createEmptyQuestion('short', idx + 1);
    q.question = qLine;
    q.answers = [answerLine ? answerLine.replace(/^(answer|jawaban)\s*:\s*/i, '') : ''];
    out.push(q);
  });
  return out;
};

export const CSV_TEMPLATE = 'type,title,question,option1,option2,option3,option4,option5,correct,answer,tolerance\n' +
  'mc-single,MC01,"Ibukota Indonesia?",Jakarta,Bandung,Surabaya,Medan,,A,,\n' +
  'mc-multiple,MCM01,"Bilangan prima?",2,3,4,5,6,A|B|D,,\n' +
  'truefalse,TF01,"Matahari bintang.",,,,,,true,,\n' +
  'short,SA01,"Kepanjangan HTML?",,,,,,,"HyperText Markup Language",\n' +
  'numerical,NUM01,"2+2=?",,,,,,,4,0\n' +
  'essay,ES01,"Jelaskan gravitasi.",,,,,,,"Gaya tarik antar massa.",';
